<template>
    <v-container fluid class="d-flex ma-0 pa-0 d-md-none">
        <v-layout row wrap class="ma-0 pa-0">

            <v-col cols="12" class="ma-0 pa-0">
                <!-- Loading message -->
                <v-card v-if="!hasProblem && isLoading"
                    class="pa-3 text-center">
                     Loading...
                     <v-progress-circular
                         indeterminate
                         color="lime"
                    ></v-progress-circular>
                </v-card>

                <!-- Error message -->
                <v-card v-if="hasProblem"
                    class="pa-3">
                    Sorry, this content is not currently available
                </v-card>

                <!-- No results message -->
                <v-card v-if="!allReservations.length && !hasProblem && !isLoading">
                    There are no enquiries or reservations to display
                </v-card>

                <!-- Results listing -->
                <v-card v-for="reservationGroup in allReservations" v-if="!hasProblem && !isLoading" flat>
                    <v-container fluid>
                        <v-layout row wrap>
                            <v-col cols="12">
                                <h2>{{ reservationGroup.title }} ({{ reservationGroup.reservations.length }})</h2>
                                {{ reservationGroup.description }}
                            </v-col>

                            <v-card
                                     v-for="(reservation, index) in reservationGroup.reservations"
                                     :key="i"
                            >
                                <v-card-text class="pa-3 ma-3">
                                        <v-layout row wrap>

                                            <v-col cols="5">
                                                <b>Reference:</b>
                                            </v-col>

                                            <v-col cols="7">
                                                <span v-if="reservation.responseIsOverdue" 
                                                      class="mr-1 circle-icon response-overdue d-none d-xl-block"></span>
                                                <b style="font-size: 100%;">{{ reservation.reference }}</b>
                                                <a :name="reservation.reference"></a>
                                                <span class="circle-icon response-overdue" 
                                                      v-if="reservation.responseIsOverdue"></span>
                                            </v-col>

                                            <!-- This field is not relevant if we are viewing just one hotel -->
                                            <v-col cols="5" v-if="!hotelkey">
                                                <b>Hotel:</b>
                                            </v-col>
                                            <v-col cols="7" v-if="!hotelkey">
                                                ({{ reservation.hotel.hotel_code }}) {{ reservation.hotel.hotel_name }}
                                            </v-col>

                                            <v-col cols="5">
                                                <b>Dates:</b>
                                            </v-col>
                                            <v-col cols="7">
                                                Arrive {{ reservation.date_arrival }}
                                                </br>
                                                Depart {{ reservation.date_departure }}
                                                </br>
                                                ({{ reservation.num_nights }} nights)
                                            </v-col>

                                            <v-col cols="5">
                                                <b>Client:</b>
                                            </v-col>
                                            <v-col cols="7">
                                                {{ reservation.client.first_name }} {{ reservation.client.last_name }}
                                            </v-col>

                                            <v-col cols="5">
                                                <b>Quote (GBP):</b>
                                            </v-col>
                                            <v-col cols="7">
                                                {{ reservation.quote_amount_gbp }}
                                            </v-col>

                                            <v-col cols="12" class="align-center">
                                                <v-icon v-if="reservation.has_voucher" title="Gift vouchers were redeemed">card_giftcard</v-icon>
                                                <v-icon v-if="reservation.has_van" title="Virtual credit card created">credit_card</v-icon>
                                                <v-icon v-if="reservation.is_prepaid" title="Full advance payment made to i-escape">paid</v-icon>
                                                <v-icon v-if="reservation.unreadMessages" title="Has unread messages">mark_chat_unread</v-icon>
                                                <v-icon v-if="reservation.is_instant" title="Instant booking">bolt</v-icon>
                                                <v-icon v-if="reservation.is_test" title="Test booking">star</v-icon>
                                            </v-col>

                                        <v-col cols="12">
                                            <v-btn class="ies-green" 
                                                   v-on:click="showReservation(reservation.reservation_key)" 
                                                   block>Respond</v-btn>
                                        </v-col>

                                    </v-layout>
                                </v-card-text>
                            </v-card>

                        </v-layout>
                    </v-container>

                </v-card>


                <v-col cols="12" 
                       class="pa-0" 
                       style="position: fixed; top:0; left: 0; width:100vw; height: 100vh; max-width: 100vw; max-height: 100vh; overflow: auto; background-color: white; z-index:100;"
                       v-if="shouldDisplayReservation">
                     <v-btn><v-icon>close</v-icon></v-btn>
				     <ies-reservation-administration-management 
				          :reservationkey="shouldDisplayReservation"></ies-reservation-administration-management>
                </v-col>
            </v-col>
        </v-layout>
    </v-container>
</template>

<script>
    /**
     * A Vue component dedicated to letting the user manage the reservations
     * for the hotel specified, or for all hotels if not specified
     *
     * Props:
     *     int 'hotelkey' - The ID of the hotel to filter by, if any
     */

    import axios from 'axios';
    export default {
        name: 'hotel-reservation-administration',
        props: ['hotelkey'],
        data () {
            return {
                /** @var {} allFacilities - The full spectrum of facilities that the i-escape system can understand */
                allReservations: [],
                emailClientUrl: '',
                dailyPlannerUrl: this.hotelkey ? "/hotels/" + this.hotelkey + "/daily-planner" : '',
                legendPanel: [],
                searchPanel: [],
                addNewPanel: [],
                shouldDisplayReservation: null,

                // View/data models for the search tool - this is submitted to the API when requesting the data
                search: {
                    client: {
                        email: "",
                        name: "",
                    },
                    hotel: {
                        hotelKey: (this.hotelkey ? parseInt(this.hotelkey) : null)
                    },
                    reservation: {
                        reference: "",
                    },
                    status: {
                        enquiries: true,
                        cancellations: true,
                        confirmed: true,
                        completed: false,
                        quoteDeclined: true,
                        waiting: true
                    }
                },
                isLoading: true,
                hasProblem: false,
                /** @var Toggles live view, periodically auto refreshing the data on the page after a defined period of time */
                liveRefresh:    false,
                /** @var Object live view timer instance */
                liveViewTimer: null,
                i: null
            }
        },

        methods: {

            /**
             * @see data allFacilities
             */
            loadAllReservations(){
                this.isLoading = true;
                try{
                axios.post('/api/hotels/all/reservation-administration', this.search)
                .then(function(response){
                    var reservations = response.data;
                    reservations.forEach(function(group){
                        group.reservations.forEach(function(reservation){
                            reservation.show = false;

                            // Determine the URLs for relevant links and emails
                            reservation.emailClientUrl = "mailto:" + reservation.client.email + '?subject=i-escape.com Reservation ' + reservation.reference + '&body=Dear ' + reservation.client.first_name + ', ';
                            reservation.phoneClientUrl = "tel:" + reservation.client.phone_number;
                            reservation.viewClientUrl  = "/myescapes-members/" + reservation.client.member_key;
                            reservation.emailHotelUrl  = "mailto:" + reservation.hotel.reservationEmails.join(';') + '?subject=i-escape.com Reservation ' + reservation.reference + '&body=Dear ' + reservation.hotel.hotel_name + ', ';
                            reservation.phoneHotelUrl = "tel:" + reservation.hotel.phone_numbers[0];
                            reservation.plannerUrl = "/hotels/" + reservation.hotel.hotel_key + "/daily-planner";
                            reservation.rateTypesUrl = "/hotels/" + reservation.hotel.hotel_key + "/rate-types/list";
                            reservation.roomTypesUrl = "/hotels/" + reservation.hotel.hotel_key + "/room-types/list";
                            reservation.specialOffersUrl = "/live-hotels-offers/list?s=as&c[]=Hotel-hotel_key&o[]===&v[]=" + reservation.hotel.hotel_key;

                            // Check if the reservation is overdue a response from the hotelier
                            reservation.responseIsOverdue = this.responseIsOverdue(reservation);

                            // View model to power the initialied-by user checkbox as the data itself is an object not a boolean
                            reservation.has_been_checked = reservation.initialed_by_user != null;
                        }.bind(this));

                        // Update the url to include any filters being applied
                        this.updateUrlFromFilters();
                    }.bind(this));
                    this.allReservations = reservations;
                    this.isLoading = false;
                }.bind(this));
                } catch(exception){
                    this.hasProblem = true;
                }
            },



            showReservation(reservationKey){
                this.shouldDisplayReservation = reservationKey;
                alert("should show");
            },


            /**
             * @method responseIsOverdue
             *
             * Method to contain the business logic which determines whether a response to
             * this reservation is overdue, and highlight it to the user.  The exact logic
             * depends on the specific status of the enquiry/reservation and must be determined
             *
             * @param reservation
             */
            responseIsOverdue(reservation){
                // Create a JS date object for probing

                var creationDate = Date.parse(reservation.date_created);
                var ageInDays = ((new Date() - creationDate) / (1000 * 3600 * 24));

                // Whether it is considered overdue is dependent upon different
                // qualities depending on what its status is
                switch(reservation.status_key){
                    // New enquiry needs a response within 48 hours
                    // Re-enquiries need a response within 24 hours
                    case 8:
                        return ageInDays > 2;
                    // Confirmed
                    case 12:
                        break;
                    // Awaiting
                    case 20:
                        break;
                    // New cancellation awaiting confirmation
                    case 17:
                        break;
                    // Cancelled (confirmed)
                    case 18:
                        break;
                    // Declined
                    case 15:
                        break;
                    // Accepted
                    case 13:
                        return ageInDays > 5;
                        break;
                    // Completed
                    case 9:
                        break;
                }
                return false;
            },

            /**
             * Trigger staff to enter the user account without authentication
             */
            logInAsClient(reservation){
                window.open("https://" + window.location.host + "/autoLogInClient?id=" + reservation.client.member_key);
            },


            dateFormatDDMMYY(dateString){
                // Currently the dateString is being supplied in the format yyyy-mm-dd so we can
                // just rearrange the parts for the most part
                var parts = dateString.split('-');
                return parts[2] + '-' + parseInt(parts[1]) + '-' + parts[0];
            },

            dateFormatDDMMMYY(dateString){
                // Currently the dateString is being supplied in the format yyyy-mm-dd so we can
                // just rearrange the parts for the most part
                var parts = dateString.split('-');
                var monthNames = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                return parts[2] + '-' + monthNames[parseInt(parts[1])] + '-' + parts[0];
            },

            createNewReservation(){
                alert("Sorry, this feature is not yet available");
            },

            /**
             * Makes a basic email mailto template to query the iescape staff member who checked and
             * signed off on the reservation
             *
             * @param reservation
             */
            makeInitialedEmailUrl(reservation){
                return "mailto:" + reservation.initialed_by_user.email + "?subject=Re:%20" + reservation.reference + "&body=Dear%20" + reservation.initialed_by_user.first_name + ",%20";
            },

            /**
             * @method toggleChecked
             *
             * Business logic performed when a user ticks or unticks the
             * initialed check box showing that they have reviewed the
             * enquiry/reservation and found it fine, or have removed the
             * previous check status because they think it has changed for
             * example
             *
             * @param reservation
             */
            toggleChecked(reservation){
                // Check what the view model says regarding the user's interaction
                // Note that the view model value may not appear to have been updated
                // by the framework from within the app yet, so if it is currently
                // set then the user is in the 'process' of unchecking it and therefore
                // we need to perform the unchecking tasks here - even though the view
                // model is still reporting it as being true
                if (reservation.has_been_checked){
                    this.unsetCheckedByUser(reservation);
                }
                else {
                    this.setCheckedByUser(reservation);
                }
            },

            setCheckedByUser(reservation){
                axios.get('/api/reservations/' + reservation.reservation_key + '/set-checked?action=check')
                .then(function(response){
                    reservation.initialed_by_user = response.data.user;
                }.bind(this));
            },

            unsetCheckedByUser(reservation){
                axios.get('/api/reservations/' + reservation.reservation_key + '/set-checked?action=uncheck')
                .then(function(response){
                    reservation.initialed_by_user = null;
                }.bind(this));
            },

            voidReservation(reservation){
                var self = this;
                if (!window.confirm("This will void the reservation.  The record will still exist, but it will not be considered a valid record and will not be displayed to the client or hotel")){
                    return;
                }

                axios.get('/api/reservation/' + reservation.reservation_key + '/set-void?action=void')
                .then(function(response){
                    alert("This reservation has now been voided and will not show to the client.\n\nPlease ensure any deposit payments are refunded and virtual cards are cancelled");
                    self.loadAllReservations();
                }.bind(this));
            },

            updateFiltersFromUrl(){
                //var baseUrl = window.location.href.split('?')[0];

                var urlSearchParams = new URLSearchParams(window.location.search)
                console.log(urlSearchParams);

                urlSearchParams.forEach(function(urlSearchParam){
                    console.log(urlSearchParam);
                });

                var statuses = urlSearchParams.getAll("s[]");
                if (statuses.length){
                    console.log(statuses);
                    // Reset to false default on all statuses
                    this.search.status = {
                        enquiries: false,
                        cancellations: false,
                        confirmed: false,
                        completed: false,
                        quoteDeclined: false,
                        waiting: false
                    }
                
                 // Only set to true if they are present in the query params
                for(var index=0; statuses && index < statuses.length; index++){
                    console.log("<F3>", statuses[index]);
                    switch (statuses[index]){
                        case "enquiries":
							this.search.status.enquiries = true;
							break;
                        case "cancellations":
							this.search.status.cancellations = true;
							break;
                        case "confirmed":
							this.search.status.confirmed = true;
							break;
                        case "completed": 
							this.search.status.completed = true;
							break;
                        case "declined": 
							this.search.status.quoteDeclined = true;
							break;
                        case "waiting":
							this.search.status.waiting = true;
							break;
                    }
                }
                }

            },

            updateUrlFromFilters(){
                console.log("update url", window.location.href);
                var queryParams = '';
                if (this.search.status.enquiries){
                    queryParams += '&s[]=enquiries';
                }
                if (this.search.status.cancellations){
                    queryParams += '&s[]=cancellations';
                }
                if (this.search.status.confirmed){
                    queryParams += '&s[]=confirmed';
                }
                if (this.search.status.completed){
                    queryParams += '&s[]=completed';
                }
                if (this.search.status.quoteDeclined){
                    queryParams += '&s[]=declined';
                }
                if (this.search.status.waiting){
                    queryParams += '&s[]=waiting';
                }
                var newUrl = window.location.href.split('?')[0] + (queryParams ? '?' + queryParams : '');
                console.log(newUrl);
                window.history.pushState({}, '', newUrl);
            }
        },

        /**
         * Once mounted this component needs to load all reservations for the
         * hotelkey specified
         */
        mounted() {
            try {
                this.updateFiltersFromUrl();

                // Obtain all reservations on page load
                this.loadAllReservations();
            } catch(exception){
                console.log(exception);
                this.hasProblem = true;
            }

            // Respond to external requests to reload the listed data
            this.$root.$on('Reservation Administration: reload', function(){
                this.loadAllReservations();
            });
        },

        computed: {},

        watch: {
            panel: function(){
                console.log('PANEL INDEX CHANGED:', this.panel);
            }
        }
    }
</script>

<style>
     .reservation-expansion-panel {
         padding: 0;
     }

    .v-expansion-panel-content__wrap { 
        padding: 0;
    }

    .expansion-panel__header {
        padding: 0;
    }

    .v-card__text {
        padding: 0 !important;
    }

    /* Highlight those records which urgently need a response from the hotelier */
    .response-overdue {
        /*background-color: #FFFABD !important; pale yellow */
        background-color: #C1CD23 !important;
    }

    .circle-icon {
        height: 25px;
        width: 25px;
        border-radius: 50%;
        display: inline-block;
    }
</style>
